import { sortStrings } from 'flow/utils/sorter-utils';
import {
    ActiveCallDirections,
    CallState,
    convertNumberToE164,
    prettyFormatE164Number,
} from 'flow/utils/call-utils';
import { prettyPersonalNumber } from 'flow/utils';
import { getOwnActiveCalls } from 'flow/selectors/call-selectors';
import { isURL } from './call-widget-utils';

const CONTACT_BAG_VARIABLE_KEY = 'CONTACT';

export const CallWidgetTypes = Object.freeze({
    BANKID_ON_DEMAND: 'bankidondemand',
});

export const validateProtocol = (url) => {
    return !!url && url.includes(':');
};

export const isTwelveDigitSocialSecurityNumber = (socialSecurityNumber) => {
    return /^[0-9]{12}$/.test(socialSecurityNumber);
};

export const isTenDigitSocialSecurityNumber = (socialSecurityNumber) => {
    return /^[0-9]{10}$/.test(socialSecurityNumber);
};

export const socialSecurityNumberToTwelveDigits = (socialSecurityNumber) => {
    if (isTwelveDigitSocialSecurityNumber(socialSecurityNumber)) {
        return socialSecurityNumber;
    } else {
        return '';
    }
};

export const socialSecurityNumberToTenDigits = (socialSecurityNumber) => {
    if (isTwelveDigitSocialSecurityNumber(socialSecurityNumber)) {
        return socialSecurityNumber.substr(2);
    } else if (isTenDigitSocialSecurityNumber(socialSecurityNumber)) {
        return socialSecurityNumber;
    } else {
        return '';
    }
};

export const socialSecurityNumberToTwelveDigitsWithDash = (
    socialSecurityNumber,
) => {
    if (isTwelveDigitSocialSecurityNumber(socialSecurityNumber)) {
        return prettyPersonalNumber(socialSecurityNumber);
    } else {
        return '';
    }
};

export const socialSecurityNumberToTenDigitsWithDash = (
    socialSecurityNumber,
) => {
    if (isTwelveDigitSocialSecurityNumber(socialSecurityNumber)) {
        return prettyPersonalNumber(socialSecurityNumber.substr(2));
    } else if (isTenDigitSocialSecurityNumber(socialSecurityNumber)) {
        return prettyPersonalNumber(socialSecurityNumber);
    } else {
        return '';
    }
};

const replaceURLVariable =
    (braceDelimiters) => (url, variable, replaceValue) => {
        const startDelimiter = braceDelimiters ? '{' : '%';
        const endDelimiter = braceDelimiters ? '}' : '%';
        return url.replace(
            `${startDelimiter}${variable}${endDelimiter}`,
            replaceValue,
        );
    };

export const populateUrl = (url, bagVariables) => {
    if (!url) {
        return null;
    }

    const braceDelimiters = url.includes('{');
    const variableMatches = braceDelimiters
        ? url.matchAll(/{(.*?)}/g)
        : url.matchAll(/%(.*?)%/g);

    const replace = replaceURLVariable(braceDelimiters);

    for (const match of variableMatches) {
        const variable = match[1];
        if (bagVariables && variable in bagVariables) {
            // Don't encode URI components if it's already a URL. Allows for passing entire URLs as variables
            const encode = !isURL(bagVariables[variable]);
            const value = encode
                ? encodeURIComponent(bagVariables[variable])
                : bagVariables[variable];
            url = replace(url, variable, value);
            continue;
        }
        const dotIndex = variable.indexOf('.');
        if (dotIndex && bagVariables) {
            const bagKey = variable.substring(0, dotIndex);
            const jsonPath = variable.substring(dotIndex + 1);
            try {
                const bagValue =
                    typeof bagVariables[bagKey] === 'object'
                        ? bagVariables[bagKey]
                        : JSON.parse(bagVariables[bagKey]);
                let value = jsonPath
                    .split('.')
                    .reduce((object, key) => object[key], bagValue);

                if (value !== undefined) {
                    // Don't encode URI components if it's already a URL. Allows for passing entire URLs as variables
                    const encode = !isURL(value);
                    value =
                        typeof value === 'object'
                            ? JSON.stringify(value)
                            : value;
                    value = encode ? encodeURIComponent(value) : value;
                    url = replace(url, variable, value);
                    continue;
                }
            } catch (_e) {
                // don't throw
            }
        }
        url = replace(url, variable, '');
    }

    return url;
};

const populateUrlList = (urlList, bagVariables) => {
    urlList = urlList || [];
    urlList.forEach(({ url }, index) => {
        urlList[index].url = populateUrl(url, bagVariables);
    });

    return urlList;
};

export const getSortedCallWidgetUrls = (callWidgets) => {
    return (
        callWidgets &&
        Object.values(callWidgets.toList().toJS()).sort((a, b) => {
            return sortStrings(a && a.title, b && b.title);
        })
    );
};

export const getSortedExternalSearchCallWidgetUrlsWithPhoneNumbers = (
    callWidgets,
    bagVariables,
) => {
    if (!callWidgets) {
        return [];
    }

    const sortedList = getSortedCallWidgetUrls(
        getExternalSearchWidgets(callWidgets),
    );

    return populateUrlList(sortedList, bagVariables);
};

export const getSortedExternalSearchCallWidgetUrlsBasedOnNumber = (
    callWidgets,
    bagVariables,
) => {
    if (!callWidgets) {
        return [];
    }
    const sortedList = getSortedCallWidgetUrls(
        getExternalSearchWidgetsForContextMenu(callWidgets),
    );

    return populateUrlList(sortedList, bagVariables);
};

export const getNumberInRunUrlFriendlyFormat = (
    number,
    loggedInUserCountryCode,
) => {
    /**
     * Converting number to e164 format if possible
     */
    const e164Number = convertNumberToE164(number, loggedInUserCountryCode);

    /**
     * Formatting to national number if number is from the same country as relativeCountryCode
     */
    const numberPrettyFormatted =
        prettyFormatE164Number(e164Number, loggedInUserCountryCode) || '';

    /**
     * Removing characters that are not digits or not the + character,
     * so that 33 00 45 99 is formatted to 33004599, 0762-11 22 33 is formatted to 0762112233, etc...
     */
    return numberPrettyFormatted.replace(/[^0-9+]/g, '');
};

export const getE164NumberInRunUrlFriendlyFormat = (
    number,
    loggedInUserCountryCode,
) => {
    /**
     * Converting number to e164 format if possible
     */
    const e164Number =
        convertNumberToE164(number, loggedInUserCountryCode) || '';

    /**
     * Removing characters that are not digits or not the + character,
     * so that 33 00 45 99 is formatted to 33004599, 0762-11 22 33 is formatted to 0762112233, etc...
     */
    return e164Number.replace(/[^0-9+]/g, '');
};

export const getBankidOnDemandWidget = (callWidgets) => {
    return (
        callWidgets &&
        callWidgets.filter(notVersion2Widgets).find((widget) => {
            return widget.get('url', '').includes('bankidagentview.jsp');
        })
    );
};

export const getExternalSearchWidgets = (callWidgets) => {
    return (
        callWidgets &&
        callWidgets.filter(notVersion2Widgets).filter((widget) => {
            return !widget.get('url', '').includes('bankidagentview.jsp');
        })
    );
};

const filterVersion2Widgets = (include) => (widget) => {
    return widget && (include ? !!widget.get('config') : !widget.get('config'));
};

export const getVersion2Widgets = (callWidgets) => {
    if (!callWidgets) {
        return [];
    } else {
        return callWidgets.filter(onlyVersion2Widgets).toList();
    }
};

export const getVersion1Widgets = (callWidgets) => {
    if (!callWidgets) {
        return [];
    } else {
        return callWidgets.filter(notVersion2Widgets).valueSeq();
    }
};

export const notVersion2Widgets = (widget) =>
    filterVersion2Widgets(false)(widget);
export const onlyVersion2Widgets = (widget) =>
    filterVersion2Widgets(true)(widget);

export const getExternalSearchWidgetsForContextMenu = (callWidgets) => {
    return (
        callWidgets &&
        callWidgets.filter((widget) => {
            const url = widget.get('url', '');
            return (
                !url.includes('bankidagentview.jsp') &&
                (url.includes('%NUMBER%') ||
                    url.includes('{NUMBER}') ||
                    url.includes(`{${CONTACT_BAG_VARIABLE_KEY}`) ||
                    url.includes(`%${CONTACT_BAG_VARIABLE_KEY}`))
            );
        })
    );
};

/**
 * Use this function to check if we should auto open call widget urls.
 * Note that this function does not open any urls, it just return a boolean
 * indicating if we should open any urls.
 */
export const shouldAutoOpenCallWidgetUrls = (state, activeCall) => {
    const ownActiveCalls = getOwnActiveCalls(state);
    const oldActiveCall = ownActiveCalls
        .filter((call) => call.get('key') === activeCall.key)
        .first();

    return (
        !!oldActiveCall &&
        oldActiveCall.get('direction') ===
            ActiveCallDirections.INCOMING.value &&
        !CallState.UP.is(oldActiveCall.get('state')) &&
        CallState.UP.is(activeCall.state) &&
        (oldActiveCall.get('source') !== undefined ||
            activeCall.source !== undefined)
    );
};

/**
 * For picked queue calls we don't want to open callwidgets until we have received the
 * QueueCallInfo event we get after the picked call has been connected. Then this
 * function should be used. The condtion for state === UP is to not open callwidgets too
 * early for regular queue calls, because then we get a QueueCallInfo event when the
 * queue makes a call attempt, before anyone answers.
 *
 * The reason we do this is because for picked calls we dont' receive the originalCallerId
 * until we get the QueueCallInfo, so before that we don't have a number to use in the widget.
 */
export const shouldAutoOpenCallWidgetUrlsForPickedQueueCall = (
    state,
    activeCall,
) => {
    const ownActiveCalls = getOwnActiveCalls(state);
    const oldActiveCall = ownActiveCalls
        .filter((call) => call.get('key') === activeCall.key)
        .first();

    return (
        !!oldActiveCall &&
        CallState.UP.is(oldActiveCall.get('state')) &&
        (activeCall.source !== undefined ||
            oldActiveCall.get('source') !== undefined)
    );
};

export const addContactToBagVariables = (contactDTO, bagVariables) => {
    const getContactAsBagVariable = (contactDTO) => {
        // Currently, we only add the contact as a bag variable if it's an integration contact.
        // NOTE: There is probably no danger with simply allowing any contact to be used in a widget.
        if (!contactDTO || !contactDTO.get('sourceId')) {
            return null;
        }

        const contact = contactDTO.toJS();
        if (typeof contact.metadata === 'string') {
            try {
                // Metadata is a string that may hold JSON data.
                // Try to parse it. If we fail, simply keep as is.
                contact.metadata = JSON.parse(contact.metadata);
            } catch (_e) {
                // Don't do anything. Leave metadata alone.
            }
        }
        return JSON.stringify(contact);
    };

    const contactBagVariable = getContactAsBagVariable(contactDTO);
    if (contactBagVariable) {
        const updatedBagVariables = bagVariables ? bagVariables : {};
        updatedBagVariables[CONTACT_BAG_VARIABLE_KEY] = contactBagVariable;
        return updatedBagVariables;
    } else {
        return bagVariables;
    }
};
